/**
 * MKS-332 — the money glossary, under Reports.
 *
 * The page a tooltip is too small for. Each figure gets its definition (the
 * same sentence the tooltip shows), the Czech word the team actually uses in
 * conversation, and the handful of things people reliably get wrong about it.
 *
 * Reads `MONEY_GLOSSARY` rather than restating the definitions, so this page
 * can never become a sixth place the vocabulary is defined — which is the
 * failure mode the whole ticket exists to fix.
 */
import { useAuth } from '../../services/authContext';
import { MONEY_GLOSSARY, MONEY_METRIC_ORDER, type MoneyMetricKey } from '../../utils/moneyGlossary';

/** Cost and margin are admin/finance-only, so their cards are too. */
const RESTRICTED: ReadonlySet<MoneyMetricKey> = new Set(['cost', 'margin']);

export default function MoneyGlossaryPage() {
  const { isAdmin, isFinance } = useAuth();
  const canSeeCost = isAdmin || isFinance;

  const metrics = MONEY_METRIC_ORDER.filter((key) => canSeeCost || !RESTRICTED.has(key));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Money glossary</h1>
        <p className="mt-1 max-w-3xl text-sm text-gray-600">
          Every money figure in Monksheets is one of these. They are computed once, in one place,
          and every report reads that same computation — so the same project and month gives the
          same number wherever you look at it.
        </p>
      </div>

      <div className="space-y-4">
        {metrics.map((key) => {
          const metric = MONEY_GLOSSARY[key];
          return (
            <section
              key={key}
              className="rounded-lg border border-gray-200 bg-white p-6"
              aria-labelledby={`glossary-${key}`}
            >
              <div className="flex flex-wrap items-baseline gap-x-3">
                <h2 id={`glossary-${key}`} className="text-lg font-medium text-gray-900">
                  {metric.label}
                </h2>
                <span className="text-sm text-gray-400">{metric.czech}</span>
                {RESTRICTED.has(key) && (
                  <span className="rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                    admin &amp; finance only
                  </span>
                )}
              </div>

              <p className="mt-2 text-sm text-gray-700">{metric.definition}</p>

              <ul className="mt-3 space-y-1.5 text-sm text-gray-600">
                {metric.notes.map((note) => (
                  <li key={note} className="flex gap-2">
                    <span aria-hidden="true" className="text-gray-300">
                      •
                    </span>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      {!canSeeCost && (
        <p className="text-sm text-gray-500">
          Cost and margin are currently visible to admin and finance only, so they are not listed
          here and do not appear on the reports.
        </p>
      )}
    </div>
  );
}
